import { computed, effect, signal } from '@preact/signals';
import { AudioState, RequestedAudioState, SleepTimer } from '../models/audio-state';
import { Episode, EpisodeCompoundId } from '../models/podcasts';
import debugLog from '../utils/debug';
import { exposeOnWindow, isEpisodeIdentifier, secondsToTime } from '../utils/helpers';
import { getInitialValue, setLazyValue } from '../utils/localstorage';
import { isWebApp } from '../utils/platform';

const log = (...args: any[]) => {
  debugLog({ logName: 'signals', logColor: 'DarkSeaGreen' }, ...args);
};

export const state = signal<AudioState>('unloaded');
export const requestedState = signal<RequestedAudioState>('');
export const currentEpisode = signal<Episode | undefined>(undefined);
export const currentEpisodeId = computed(() => currentEpisode.value?.id);

export const currentDuration = signal<number>(NaN);
export const currentSeek = signal<number>(0);
export const requestedSeek = signal<number>(NaN);
export const currentError = signal<string>('');
export const isFixingAudio = signal<boolean>(false);
export const sleepTimer = signal<SleepTimer>('');

// Playback rate (stored locally)
export const playbackRate = signal<number>(
  getInitialValue(
    'SignalStore__playbackRate',
    (rate) => typeof rate === 'number' && rate >= 0.5 && rate <= 3,
    1.0
  )
);
effect(() => {
  setLazyValue('SignalStore__playbackRate', playbackRate.value);
});

// Current playlist (stored locally on web and natively on newer iOS / Android versions)
export const currentPlaylist = signal<[EpisodeCompoundId, number][]>(
  isWebApp
    ? getInitialValue(
        'SignalStore__currentPlaylist',
        (playlist) =>
          Array.isArray(playlist) &&
          playlist.every(
            (item) =>
              Array.isArray(item) &&
              isEpisodeIdentifier(item[0]) &&
              typeof item[1] === 'number'
          ),
        []
      )
    : []
);
if (isWebApp)
  effect(() => {
    setLazyValue('SignalStore__currentPlaylist', currentPlaylist.value);
  });

let lastStateString = '';
effect(() => {
  // Reset requestedState if it equals the current state
  if (state.value === requestedState.value) {
    requestedState.value = '';
  }

  // Log state change
  const stateString = `State: ${state.value} ${
    requestedState.value ? `-> ${requestedState.value}` : ''
  } (seek = ${secondsToTime(currentSeek.peek(), 2)})`;
  if (stateString !== lastStateString) {
    log(stateString);
    lastStateString = stateString;
  }

  // Reset error when leaving error state
  if (state.value !== 'error') currentError.value = '';

  // Reset requestedSeek when leaving seeking state
  if (state.value !== 'seeking') requestedSeek.value = NaN;
});

export function getState() {
  return state.value;
}
export function getRequestedState() {
  return requestedState.value;
}
export function getCurrentSeek() {
  return currentSeek.value;
}
export function getRequestedSeek() {
  return requestedSeek.value;
}
export function getCurrentDuration() {
  return currentDuration.value;
}
export function getCurrentEpisode() {
  return currentEpisode.value;
}

// Expose previous state
const previousStates: (AudioState | undefined)[] = [];
effect(() => {
  previousStates.push(state.value);
  if (previousStates.length > 2) previousStates.shift();
});
export function getPreviousState() {
  return previousStates[0];
}

// Expose to window for debugging
exposeOnWindow({
  debugSignals: {
    state,
    requestedState,
    currentEpisode,
    currentDuration,
    currentSeek,
    requestedSeek,
    currentError,
  },
});
